/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, HStack, VStack, Portal, IconButton, useBreakpointValue } from 'chakra-v3';
import { Text } from '../../components/Text';
import { MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { MdOutlineOpenInNew } from 'react-icons/md';
import { EVALS_PLATFORM_URL } from '../../utils/env';

export type PipelineStep = {
  number: string;
  title: string;
  bgColor: '#f3fcf8' | '#f9fcf3';
  expandedContent: React.ReactNode;
  hasLearnMoreButton?: boolean;
};

interface PipelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: PipelineStep | null;
  currentStepIndex: number;
  totalSteps: number;
  onPrevious: () => void;
  onNext: () => void;
}

const PipelineModal: React.FC<PipelineModalProps> = ({
  isOpen,
  onClose,
  currentStep,
  currentStepIndex,
  totalSteps,
  onPrevious,
  onNext,
}) => {
  // Hooks must be called before any early returns
  const showHere = useBreakpointValue({ base: false, sm: true });
  const closeIconSize = useBreakpointValue({ base: 24, md: 32, lg: 48 }) ?? 24;
  const chevronIconSize = useBreakpointValue({ base: 20, md: 22, lg: 24 }) ?? 20;

  if (!isOpen || !currentStep) return null;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  return (
    <Portal>
      {/* Backdrop */}
      <Box
        position="fixed"
        inset={0}
        bg="rgba(0, 0, 0, 0.6)"
        zIndex={2000}
        onClick={onClose}
        opacity={isOpen ? 1 : 0}
        transition="opacity 0.3s ease-in-out"
      />

      {/* Modal Container */}
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex={2001}
        w={{ base: '95vw', sm: '90vw', md: '700px', lg: '800px' }}
        h={{ base: 'auto', md: 'auto', lg: '867px' }}
        maxH="90vh"
        minH={{ base: 'auto', md: '500px', lg: '867px' }}
        bg={currentStep.bgColor}
        border="0.8px solid"
        borderColor="rgba(10, 49, 60, 0.1)"
        borderRadius="10px"
        shadow="0px 4px 24px rgba(0, 0, 0, 0.16)"
        p={{ base: 4, sm: 5, md: 6, lg: 10 }}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={{ base: '24px', md: '32px', lg: '40px' }}
        overflowY="auto"
      >
        {/* Header Section */}
        <VStack gap={{ base: '6px', md: '8px' }} alignItems="flex-start" w="100%">
          <HStack gap={{ base: '8px', md: '12px' }} alignItems="center" w="100%">
            {/* Step Number */}
            <Text
              fontFamily="heading"
              fontSize={{ base: '18px', md: '22px', lg: '24px' }}
              fontWeight="normal"
              lineHeight="normal"
              letterSpacing="-1px"
              color="#0a313c"
              flexShrink={0}
            >
              {currentStep.number}
            </Text>

            {/* Vertical Separator */}
            <Box
              h={{ base: '30px', md: '35px' }}
              w="0px"
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Box
                h="1px"
                w={{ base: '30px', md: '35px' }}
                bg="rgba(10, 49, 60, 0.2)"
                transform="rotate(90deg)"
                position="absolute"
              />
            </Box>

            {/* Title */}
            <Text
              fontFamily="heading"
              fontSize={{ base: '18px', md: '22px', lg: '24px' }}
              fontWeight="normal"
              lineHeight="normal"
              letterSpacing="-1px"
              color="#0a313c"
              flex="1"
              whiteSpace="pre-wrap"
              minW={0}
            >
              {currentStep.title}
            </Text>

            {/* Close Button */}
            <IconButton
              aria-label="Close modal"
              variant="ghost"
              onClick={onClose}
              color="#0a313c"
              minW={{ base: '36px', md: '40px' }}
              h={{ base: '36px', md: '40px' }}
              flexShrink={0}
            >
              <MdClose size={closeIconSize} color="#0a313c" />
            </IconButton>
          </HStack>

          {/* Horizontal Divider */}
          <Box h="1px" w="100%" bg="rgba(10, 49, 60, 0.2)" />
        </VStack>

        {/* Content Area */}
        <VStack gap={{ base: '24px', md: '32px', lg: '40px' }} alignItems="flex-start" w="100%" flex="1">
          <Box
            w="100%"
            fontFamily="body"
            fontSize={{ base: '14px', sm: '15px', md: '15px', lg: '16px' }}
            fontWeight="normal"
            lineHeight="normal"
            color="#1a1a1a"
          >
            {currentStep.expandedContent}
          </Box>

          {/* Learn More Button (only for step 03) */}
          {currentStep.hasLearnMoreButton && (
            <Box
              as="button"
              bg="rgba(255, 255, 255, 0.5)"
              border="0.8px solid"
              borderColor="rgba(10, 49, 60, 0.1)"
              borderRadius="4px"
              h="48px"
              w={{ base: '100%', md: '402px' }}
              px="8px"
              py="8px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="8px"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(EVALS_PLATFORM_URL, '_blank', 'noopener,noreferrer');
                }
              }}
              _hover={{ opacity: 0.9 }}
              cursor="pointer"
            >
              <Text
                fontFamily="body"
                fontSize={{ base: '14px', sm: '15px', md: '16px' }}
                fontWeight="normal"
                lineHeight="1.4"
                color="#006ca7"
              >
                Learn More about Karya Evals Platform{showHere ? ' here' : ''}
              </Text>
              <MdOutlineOpenInNew size={24} color="#006ca7" />
            </Box>
          )}
        </VStack>

        {/* Footer Navigation */}
        <HStack w="100%" justify="space-between" alignItems="center" pt={{ base: 2, md: 4 }}>
          {/* Previous Button */}
          {!isFirstStep ? (
            <HStack
              as="button"
              gap={{ base: '8px', md: '12px' }}
              alignItems="center"
              px={0}
              py={{ base: '6px', md: '8px' }}
              bg="transparent"
              border="none"
              cursor="pointer"
              onClick={onPrevious}
              _hover={{ opacity: 0.8 }}
            >
              <MdChevronLeft size={chevronIconSize} color="#0a313c" />
              <Text
                fontFamily="body"
                fontSize={{ base: '14px', md: '15px', lg: '16px' }}
                fontWeight="600"
                lineHeight="normal"
                color="#0a313c"
              >
                Previous
              </Text>
            </HStack>
          ) : (
            <Box />
          )}

          {/* Next Button */}
          {!isLastStep ? (
            <HStack
              as="button"
              gap={{ base: '8px', md: '12px' }}
              alignItems="center"
              px={0}
              py={{ base: '6px', md: '8px' }}
              bg="transparent"
              border="none"
              cursor="pointer"
              onClick={onNext}
              _hover={{ opacity: 0.8 }}
            >
              <Text
                fontFamily="body"
                fontSize={{ base: '14px', md: '15px', lg: '16px' }}
                fontWeight="600"
                lineHeight="normal"
                color="#0a313c"
              >
                Next
              </Text>
              <MdChevronRight size={chevronIconSize} color="#0a313c" />
            </HStack>
          ) : (
            <Box />
          )}
        </HStack>
      </Box>
    </Portal>
  );
};

export default PipelineModal;
